import axios from './axios-with-authentication';
import * as backend from './backend-types';
import { AppError, ERROR_CODE, userMessages } from '../util/AppError';
import { API_BASE_PATH } from './constants';
import { getCurrentUser } from './auth-user';
import { getPortalOrganizationId } from './portal-adapter';

export type ResolvedPortalPairingContext = {
  hcoId: string;
  client: backend.PortalClientResponse;
  clients: backend.PortalClientResponse[];
};

function findClientByExternalPatientId(
  clients: backend.PortalClientResponse[],
  hospitalPatientId: string,
): backend.PortalClientResponse | undefined {
  // Match case-insensitively: the backend treats external patient IDs that differ
  // only in case as the same patient, so a case-sensitive lookup here would miss
  // the existing client and wrongly try to create a duplicate.
  const target = hospitalPatientId.toLowerCase();
  return clients.find(
    (entry) =>
      String(entry.externalPatientId || '')
        .trim()
        .toLowerCase() === target,
  );
}

async function fetchClientsForHco(hcoId: string): Promise<backend.PortalClientResponse[]> {
  const { data: clients } = await axios.get<backend.PortalClientResponse[]>(
    `${API_BASE_PATH}/clients`,
    {
      params: { hcoId },
    },
  );
  return clients;
}

export function resolveAssignedClientIdForDevice(
  clients: backend.PortalClientResponse[],
  deviceId: string,
): string | undefined {
  const owner = clients.find((entry) => entry.personalData?.device?.id === deviceId);
  return owner?.id;
}

export function resolveAssignedDeviceIdForClient(
  client: backend.PortalClientResponse,
): string | undefined {
  return client.personalData?.device?.id;
}

export async function resolvePortalPairingContext(
  hospitalPatientId: string,
): Promise<ResolvedPortalPairingContext> {
  const normalizedPatientId = String(hospitalPatientId || '').trim();
  const user = await getCurrentUser();
  const hcoId = getPortalOrganizationId(user);
  if (!hcoId) {
    throw new AppError(ERROR_CODE.ERR_BAD_ORGANIZATION_ID, userMessages[ERROR_CODE.ERR_UNKNOWN]);
  }

  const clients = await fetchClientsForHco(hcoId);
  const existingClient = findClientByExternalPatientId(clients, normalizedPatientId);
  if (existingClient) {
    return { hcoId, client: existingClient, clients };
  }

  throw new AppError(ERROR_CODE.ERR_BAD_PATIENTID, userMessages[ERROR_CODE.ERR_BAD_PATIENTID]);
}
